/**
 * AutoDealReview.tsx — INF-126
 *
 * Fetches pending auto-created stub deals (stage='new-lead-auto', archived=false)
 * and renders a review panel with Confirm / Reject buttons.
 *
 * Hidden when there are no pending stubs. Sits above the main pipeline content.
 */

import { useCallback, useEffect, useState } from 'react';

interface AutoDeal {
  qt_deal_id: string;
  name: string | null;
  auto_origin_conf_score: number | null;
  contact_id: number | null;
  mirrored_at: string;
}

type ReviewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; deals: AutoDeal[] };

function formatConfidence(score: number | null): string {
  if (score === null || score === undefined) return '—';
  return `${Math.round(score * 100)}%`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface ActionState {
  [qt_deal_id: string]: 'confirming' | 'rejecting' | 'done';
}

export function AutoDealReview(): JSX.Element | null {
  const [state, setState] = useState<ReviewState>({ kind: 'loading' });
  const [actions, setActions] = useState<ActionState>({});

  const fetchPending = useCallback(() => {
    setState({ kind: 'loading' });
    const params = new URLSearchParams({
      stage: 'new-lead-auto',
      archived: 'false',
      limit: '50',
    });
    void (async () => {
      try {
        const res = await fetch(`/api/deals?${params.toString()}`, {
          credentials: 'include',
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setState({ kind: 'error', message: body.error ?? `Request failed: ${res.status}` });
          return;
        }
        const json = (await res.json()) as { deals: AutoDeal[] };
        // Filter client-side for auto_origin deals — deals-read.ts doesn't expose
        // auto_origin yet, but the stage filter already narrows to stubs only.
        setState({ kind: 'ready', deals: json.deals });
      } catch (err) {
        setState({ kind: 'error', message: (err as Error).message });
      }
    })();
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleAction = useCallback(
    async (qt_deal_id: string, action: 'confirm' | 'reject') => {
      setActions((prev) => ({
        ...prev,
        [qt_deal_id]: action === 'confirm' ? 'confirming' : 'rejecting',
      }));
      try {
        const res = await fetch(`/api/deals/${encodeURIComponent(qt_deal_id)}/${action}`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          alert(`Failed to ${action}: ${body.error ?? res.status}`);
          setActions((prev) => {
            const next = { ...prev };
            delete next[qt_deal_id];
            return next;
          });
          return;
        }
        // Mark done then remove from list after a brief visual pause.
        setActions((prev) => ({ ...prev, [qt_deal_id]: 'done' }));
        setTimeout(() => {
          setState((prev) => {
            if (prev.kind !== 'ready') return prev;
            return { kind: 'ready', deals: prev.deals.filter((d) => d.qt_deal_id !== qt_deal_id) };
          });
          setActions((prev) => {
            const next = { ...prev };
            delete next[qt_deal_id];
            return next;
          });
        }, 600);
      } catch (err) {
        alert(`Failed to ${action}: ${(err as Error).message}`);
        setActions((prev) => {
          const next = { ...prev };
          delete next[qt_deal_id];
          return next;
        });
      }
    },
    [],
  );

  // Hide entirely when no pending stubs.
  if (state.kind === 'ready' && state.deals.length === 0) return null;

  return (
    <div className="rounded-lg ring-1 ring-indigo-200 bg-indigo-50 overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-indigo-200">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-indigo-900">Auto-created — review</span>
          {state.kind === 'ready' && state.deals.length > 0 && (
            <span className="rounded-full bg-indigo-600 text-white text-xs font-medium px-2 py-0.5">
              {state.deals.length}
            </span>
          )}
        </div>
        <p className="text-xs text-indigo-700">
          SMS quote-intent signals above confidence threshold.
        </p>
      </div>

      {state.kind === 'loading' && (
        <div className="px-4 py-3 text-sm text-indigo-700">Loading…</div>
      )}

      {state.kind === 'error' && (
        <div className="px-4 py-3 text-sm text-rose-600">Error: {state.message}</div>
      )}

      {state.kind === 'ready' && state.deals.length > 0 && (
        <ul className="divide-y divide-indigo-100">
          {state.deals.map((deal) => {
            const actionState = actions[deal.qt_deal_id];
            const isDone = actionState === 'done';
            const isInFlight = actionState === 'confirming' || actionState === 'rejecting';
            return (
              <li
                key={deal.qt_deal_id}
                className={`px-4 py-3 flex items-center justify-between gap-4 transition-opacity ${
                  isDone ? 'opacity-40' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {deal.name ?? '(unnamed stub)'}
                    {deal.contact_id !== null && (
                      <a
                        href={`/contacts/${deal.contact_id}`}
                        className="ml-2 text-xs text-indigo-600 hover:underline font-normal"
                      >
                        view contact
                      </a>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Confidence: {formatConfidence(deal.auto_origin_conf_score)} ·{' '}
                    {formatDate(deal.mirrored_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={isInFlight || isDone}
                    onClick={() => void handleAction(deal.qt_deal_id, 'confirm')}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {actionState === 'confirming' ? 'Confirming…' : isDone ? 'Done' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    disabled={isInFlight || isDone}
                    onClick={() => void handleAction(deal.qt_deal_id, 'reject')}
                    className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {actionState === 'rejecting' ? 'Rejecting…' : isDone ? 'Done' : 'Reject'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
